import { ThreadMessage } from "@jace/api";
import { type StoreApi, createStore } from "zustand";

export interface MessageReplayState {
	replayIndex: number | null;
	isReplayMode: boolean;
	isAutoReplaying: boolean;
	replayTimerId: number | null;
	isMinimized: boolean;
}

export interface MessageReplayActions {
	setReplayIndex: (index: number | null) => void;
	toggleReplayMode: () => void;
	getFilteredMessages: (messages: ThreadMessage[]) => ThreadMessage[];
	startAutoReplay: (messages: ThreadMessage[]) => void;
	stopAutoReplay: () => void;
	isReplayActive: () => boolean;
	toggleMinimized: () => void;
}

export type MessageReplayStore = MessageReplayState & MessageReplayActions;

export const createMessageReplayStore = (): StoreApi<MessageReplayStore> =>
	createStore<MessageReplayStore>((set, get) => ({
		replayIndex: null,
		isReplayMode: false,
		isAutoReplaying: false,
		replayTimerId: null,
		isMinimized: false,
		setReplayIndex: (index) => {
			set({ replayIndex: index });
		},
		toggleReplayMode: () => {
			const { isReplayMode, isAutoReplaying } = get();

			// If turning off replay mode, make sure to stop any auto replay
			if (isReplayMode && isAutoReplaying) {
				get().stopAutoReplay();
			}

			set((state) => ({ isReplayMode: !state.isReplayMode }));
		},
		getFilteredMessages: (messages) => {
			const { replayIndex, isReplayMode } = get();
			if (!isReplayMode || replayIndex === null) return messages;
			return messages.slice(0, replayIndex + 1);
		},
		startAutoReplay: (messages) => {
			const { stopAutoReplay } = get();

			// Stop any existing replay
			stopAutoReplay();

			if (messages.length === 0) return;

			// Start from the first message
			set({
				replayIndex: 0,
				isReplayMode: true,
				isAutoReplaying: true,
			});

			// Function to advance to the next message
			const advanceMessage = () => {
				const { replayIndex, isAutoReplaying } = get();

				// Stop if replay was cancelled or we're at the end
				if (
					!isAutoReplaying ||
					replayIndex === null ||
					replayIndex >= messages.length - 1
				) {
					stopAutoReplay();
					return;
				}

				// Get current and next message
				const currentMsg = messages[replayIndex];
				const nextMsg = messages[replayIndex + 1];

				// Calculate wait time between messages based on timestamps
				let waitTime = 0;
				if (currentMsg.timestamp && nextMsg.timestamp) {
					const currentTime = new Date(currentMsg.timestamp).getTime();
					const nextTime = new Date(nextMsg.timestamp).getTime();
					const timeDiff = nextTime - currentTime;

					// Speed up 2x, but cap at 10 seconds for gaps longer than 1 minute
					waitTime = timeDiff > 60000 ? 10000 : timeDiff / 2;
				} else {
					// Default to a small wait if timestamps are not available
					waitTime = 500;
				}

				// Schedule the next message
				const timerId = window.setTimeout(() => {
					set({ replayIndex: replayIndex + 1 });
					advanceMessage();
				}, waitTime);

				set({ replayTimerId: timerId });
			};

			// Start the replay process
			advanceMessage();
		},
		stopAutoReplay: () => {
			const { replayTimerId } = get();
			if (replayTimerId !== null) {
				window.clearTimeout(replayTimerId);
			}
			set({
				isAutoReplaying: false,
				replayTimerId: null,
			});
		},
		isReplayActive: () => {
			const { isReplayMode, isAutoReplaying } = get();
			return isReplayMode && isAutoReplaying;
		},
		toggleMinimized: () => {
			set((state) => ({ isMinimized: !state.isMinimized }));
		},
	}));
